import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Chip, CircularProgress, Divider, Paper, Typography,
} from '@mui/material';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import PaidIcon from '@mui/icons-material/Paid';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SettingsIcon from '@mui/icons-material/Settings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import Link from '@mui/material/Link';

/**
 * One member's subscription history, in the order it happened.
 *
 * <p>The backend assembles this from the audit trail, the billing cycles and the feedback queue —
 * records that were always there but spread across four screens. This renders the result; it does
 * not do any joining of its own.
 */

const CATEGORY = {
  SUBSCRIPTION: { colour: 'primary.main', Icon: EventRepeatIcon },
  PAYMENT: { colour: 'success.main', Icon: PaidIcon },
  CONTENT: { colour: 'info.main', Icon: LockOpenIcon },
  SHIPMENT: { colour: 'warning.main', Icon: LocalShippingIcon },
  SUPPORT: { colour: 'secondary.main', Icon: SupportAgentIcon },
  SYSTEM: { colour: 'text.disabled', Icon: SettingsIcon },
};

const fallback = { colour: 'text.disabled', Icon: SettingsIcon };

const money = (value) => (value === null || value === undefined
  ? null
  : `$${Number(value).toFixed(2)}`);

const when = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value)
    : d.toLocaleString(undefined, {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
};

/**
 * What a period's billing status means to a human.
 *
 * The cycle's own SHIPPED means "the book order was raised", not "the books went out" — the order's
 * real state is shown beside it — so the payment chip never claims a shipment of its own.
 */
const BILLING = {
  CHARGED: { label: 'Paid', colour: 'success' },
  SHIPPED: { label: 'Paid', colour: 'success' },
  CLOSED: { label: 'Paid · closed', colour: 'success' },
  BLOCKED: { label: 'Blocked', colour: 'error' },
  PRICED: { label: 'Priced', colour: 'default' },
  NOTIFIED: { label: 'Notified', colour: 'default' },
  PENDING: { label: 'Pending', colour: 'default' },
};

/** The book order's own milestone. Anything before SHIPPED has not left the warehouse. */
const BOOKS = {
  PENDING: { label: 'Books pending', colour: 'default' },
  PAID: { label: 'Books ordered', colour: 'default' },
  PROCESSING: { label: 'Books processing', colour: 'default' },
  SHIPPED: { label: 'Books shipped', colour: 'success' },
  OUT_FOR_DELIVERY: { label: 'Books out for delivery', colour: 'success' },
  DELIVERED: { label: 'Books delivered', colour: 'success' },
  CANCELLED: { label: 'Books cancelled', colour: 'error' },
  CANCELED: { label: 'Books cancelled', colour: 'error' },
};

const day = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toDateString();
};

const MemberTimelinePanel = ({ iboNumber, callApi }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!iboNumber) return;
    setLoading(true);
    setError('');
    try {
      setData(await callApi(`/api/v1/content/admin/members/${iboNumber}/timeline`));
    } catch (e) {
      setError(e.message || 'Could not load this member’s history');
    } finally {
      setLoading(false);
    }
  }, [iboNumber, callApi]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, textAlign: 'center' }}>
        <CircularProgress size={22} />
      </Paper>
    );
  }

  if (error) {
    return <Alert severity="warning" onClose={() => setError('')}>{error}</Alert>;
  }

  if (!data) return null;

  const { summary, periods = [], events = [] } = data;

  return (
    <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: summary?.subscriptionCount > 1 ? 0.25 : 1.5 }}>
        History
      </Typography>
      {/* This panel is scoped to the member, not to the subscription the drawer was opened for.
          Only worth saying when they actually have more than one. */}
      {summary?.subscriptionCount > 1 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
          Across all {summary.subscriptionCount} subscriptions for this member
        </Typography>
      )}

      {summary && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          <Chip size="small" variant="outlined"
            label={`Paid ${money(summary.lifetimeCharged) ?? '$0.00'}`} />
          <Chip size="small" variant="outlined"
            label={`${summary.periodsCharged ?? 0} period(s)`} />
          <Chip size="small" variant="outlined"
            label={`${summary.booksShipped ?? 0} shipment(s)`} />
          {summary.openTickets > 0 && (
            <Chip size="small" color="secondary"
              label={`${summary.openTickets} open ticket(s)`} />
          )}
        </Box>
      )}

      {periods.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
            Billing periods
          </Typography>
          <Box sx={{ mt: 0.75, mb: 2 }}>
            {periods.map((p) => (
              <Box key={p.periodKey}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, py: 0.5,
                  borderBottom: '1px dashed', borderColor: 'divider',
                }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 72 }}>
                  {p.periodKey}
                </Typography>
                <Chip size="small" variant="outlined"
                  label={(BILLING[p.status] || {}).label || p.status || '—'}
                  color={(BILLING[p.status] || {}).colour || 'default'} />
                {p.bookOrderStatus && (
                  <Chip size="small" variant="outlined"
                    icon={<LocalShippingIcon />}
                    label={(BOOKS[p.bookOrderStatus] || {}).label
                      || `Books ${p.bookOrderStatus}`}
                    color={(BOOKS[p.bookOrderStatus] || {}).colour || 'default'}
                    title={p.bookOrderNumber
                      ? `${p.bookOrderNumber}${p.bookShippedAt ? ` · shipped ${when(p.bookShippedAt)}` : ''}`
                      : undefined} />
                )}
                {/* Each period keeps its own receipt, so both months are reachable. */}
                {p.receiptUrl && (
                  <Link href={p.receiptUrl} target="_blank" rel="noopener noreferrer"
                    variant="caption"
                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                    <ReceiptLongIcon sx={{ fontSize: 14 }} />
                    {p.receiptNumber || 'Receipt'}
                  </Link>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {money(p.charged) ?? money(p.total) ?? '—'}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Typography variant="caption" color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 600 }}>
        Activity
      </Typography>

      {events.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nothing recorded for this member yet.
        </Typography>
      ) : (
        <Box sx={{ mt: 1 }}>
          {events.map((e, i) => {
            const { colour, Icon } = CATEGORY[e.category] || fallback;
            const newDay = i === 0 || day(e.at) !== day(events[i - 1].at);
            return (
              <React.Fragment key={`${e.at}-${e.title}-${i}`}>
                {newDay && i > 0 && <Divider sx={{ my: 1 }} />}
                <Box sx={{ display: 'flex', gap: 1.25, py: 0.75 }}>
                  {/* The rail: a dot per event, tinted by what kind of thing happened. */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.25 }}>
                    <Icon sx={{ fontSize: 16, color: colour }} />
                    {i < events.length - 1 && (
                      <Box sx={{ flexGrow: 1, width: '1px', bgcolor: 'divider', mt: 0.5, minHeight: 12 }} />
                    )}
                  </Box>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.title}</Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      {e.amount !== null && e.amount !== undefined && (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{money(e.amount)}</Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {when(e.at)}{e.detail ? ` · ${e.detail}` : ''}
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

export default MemberTimelinePanel;
